import { HttpRequest } from '@angular/common/http';
import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { CUSTOM_BEARER_TOKEN_INTERCEPTOR_CONFIG, provideKeycloak } from '@inoventoryapps/keycloak-angular';
import Keycloak, { KeycloakInitOptions } from '@inoventoryapps/keycloak-js';
import { environment } from '../../environments/environment';
import { SERVICE_URL } from '../constants/url.constants';
import { mobileBridge } from '../services/mobile/mobile-bridge';

export function provideKeycloakConfig(): (
    | EnvironmentProviders
    | {
          provide: typeof CUSTOM_BEARER_TOKEN_INTERCEPTOR_CONFIG;
          useValue: {
              bearerPrefix: string;
              shouldAddToken: (req: HttpRequest<unknown>) => Promise<boolean>;
          }[];
      }
)[] {
    return [
        provideKeycloak({
            config: {
                url: environment.keycloak.issuer,
                realm: environment.keycloak.realm,
                clientId: environment.keycloak.clientId,
            },
        }),
        provideAppInitializer(async () => {
            const keycloak = inject(Keycloak);

            keycloak.onTokenExpired = () => {
                keycloak
                    .updateToken(30)
                    .then((refreshed) => {
                        if (refreshed) {
                            console.log('Keycloak token refreshed successfully');
                        }
                    })
                    .catch((error) => {
                        console.error('Error refreshing Keycloak token:', error);
                    });
            };

            // A failed refresh is otherwise silent but destructive: on a 400 keycloak-js
            // calls clearToken(), which sets authenticated = false, and
            // customBearerTokenInterceptor then attaches neither Authorization nor DPoP —
            // so the next API call fails as a bare 401 with no obvious cause.
            keycloak.onAuthRefreshError = () => {
                console.error(
                    'Keycloak token refresh failed; the session may have been cleared. Subsequent requests will be sent unauthenticated.',
                    { authenticated: keycloak.authenticated, hasToken: !!keycloak.token },
                );
            };

            // The URL is the only platform signal available this early: the native host
            // may not have injected window.Callback yet, so isPWA() cannot be trusted
            // at bootstrap. AppComponent uses the same check.
            const onMobileRoute = window.location.pathname.startsWith('/mobile');

            if (onMobileRoute) {
                await mobileBridge.waitForBridge();
            }

            const profile = mobileBridge.isPWA() ? await mobileBridge.getUserProfile() : undefined;
            const tokens = profile?.tokens;

            if (tokens) {
                keycloak.clientId = profile.clientId || environment.keycloak.clientId;
            }

            const initOptions: KeycloakInitOptions = {
                flow: 'standard',
                pkceMethod: 'S256',
                checkLoginIframe: false,
                scope: 'openid profile email phone',
                dpopEnabled: true,
                encryptionEnabled: environment.enableEncryption,
            };

            // On /mobile the native host owns the session. `check-sso` is what ends in a
            // full Keycloak redirect: keycloak-js falls back to onLoad() both when there
            // are no seeded tokens and when refreshing a seeded token fails, which would
            // navigate the WebView off the PWA. Omitting onLoad makes that a no-op and
            // leaves the guard to route to the unauthorized page instead.
            if (!onMobileRoute) {
                initOptions.onLoad = 'check-sso';
                initOptions.silentCheckSsoRedirectUri = window.location.origin + '/silent-check-sso.html';
            }

            if (tokens) {
                initOptions.token = tokens.token;
                initOptions.refreshToken = tokens.refreshToken;
                initOptions.idToken = tokens.idToken;
                initOptions.sigKeyPair = tokens.sigKeyPair;

                // Required whenever tokens are seeded. keycloak-js only derives timeSkew
                // from a network round trip (keycloak.js:1196), so a seeded session leaves
                // it null — and `isTokenExpired()` hard-returns true when it is null
                // (keycloak.js:810-812). Every request would then believe the token is
                // expired and force a refresh. Trust the device clock against `exp`
                // instead; the native host has just issued these tokens.
                initOptions.timeSkew = 0;
            }

            // keycloak-js forces a full `updateToken(-1)` from inside `init()` whenever it
            // is handed both a token and a refreshToken while the login iframe is off
            // (keycloak.js:301-330). On /mobile that is wrong twice over: the native host
            // has just handed us live tokens, and the refresh it fires is a raw credentialed
            // XHR (keycloak.js:855-865) outside the Angular interceptor chain — it carries no
            // subscription key, is subject to WebView CORS, and is DPoP-signed with the
            // seeded native key. When it fails, init() rejects, and on a 400 keycloak-js also
            // calls clearToken() and wipes the session the native host just supplied.
            //
            // Suppress only that one call. `setToken()` has already run by then, so token,
            // tokenParsed, authenticated and the role claims are all populated, and
            // customBearerTokenInterceptor still refreshes on demand once the token nears
            // expiry — so a genuinely stale token is still recoverable.
            const suppressInitRefresh = onMobileRoute && !!tokens;
            const forcedUpdateToken = keycloak.updateToken;

            if (suppressInitRefresh) {
                keycloak.updateToken = () => Promise.resolve(false);
            }

            try {
                await keycloak.init(initOptions);
            } catch (error) {
                console.error('Keycloak initialization failed', error);
            } finally {
                if (suppressInitRefresh) {
                    keycloak.updateToken = forcedUpdateToken;
                }
            }

            if (suppressInitRefresh) {
                const expiry = keycloak.tokenParsed?.exp;
                const secondsLeft = expiry ? expiry - Math.floor(Date.now() / 1000) : undefined;
                if (secondsLeft !== undefined && secondsLeft <= 0) {
                    console.warn(
                        'Mobile: the native host seeded an already-expired access token; the first API call will attempt a real refresh',
                    );
                }
            }
        }),
        {
            provide: CUSTOM_BEARER_TOKEN_INTERCEPTOR_CONFIG,
            useValue: [
                {
                    bearerPrefix: 'DPoP',
                    shouldAddToken: async (req: HttpRequest<unknown>) => {
                        // Same-origin static assets need no token. Every API call is
                        // built from an absolute environment.*BaseUrl, so a non-absolute
                        // URL is always a local asset.
                        if (!/^https?:\/\//i.test(req.url)) {
                            return false;
                        }

                        const excludedUrls = [
                            'cdn.lokos.in',
                            '/lokoscdn.blob.core.windows.net/',
                            '/signer',
                            SERVICE_URL.API_SETU,
                        ];
                        return !excludedUrls.some((url) => req.url.includes(url));
                    },
                },
            ],
        },
    ];
}
